<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<template>
    <Head :title="t('connections.list.title')" />
    <BaseLayout v-if="!teams_enabled" gap="lg">
        <BaseNotification
            v-if="showImportBanner"
            theme="success"
            :text="t('connections.list.banners.calendarImported', { count: calendarImportCount }, calendarImportCount)"
            @dismiss="dismissBanner" />
        <BaseNotification
            v-if="showCsvImportBanner"
            theme="success"
            :text="t('connections.list.banners.csvImported', { count: csvImportCount }, csvImportCount)"
            @dismiss="dismissCsvBanner" />
        <BaseNotification v-if="showBulkDeleteErrorBanner" theme="error" :text="t('people.bulkDelete.error')" @dismiss="showBulkDeleteErrorBanner = false" />
        <div class="flex flex-col gap-2 md:gap-2.5">
            <BasePageHeader data-testid="connections-page-header" :title="t('connections.list.title')">
                <template v-if="showEmptyState" #subtitle>
                    {{ t("connections.list.header.emptySubtitle") }}
                </template>
                <template v-else #subtitle>
                    <span>{{ t("connections.list.header.tracking") }}</span>
                    <br />
                    <span>{{ t("connections.list.header.autoAdd") }}</span>
                    <br />
                    <span>
                        {{ t("connections.list.header.privacy") }}
                        <a
                            href="https://support.valence.co/en/articles/11785623-introducing-people-in-nadia"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="base-link inline-flex items-center gap-1 ml-1"
                            @click="handleHelpLinkClick">
                            {{ t("connections.list.header.learnMore") }}
                            <i class="bi bi-box-arrow-up-right text-xs" aria-hidden="true"></i>
                            <span class="sr-only">{{ t("connections.list.header.opensInNewTab") }}</span>
                        </a>
                    </span>
                    <div v-if="public_profile_enabled" class="block md:hidden mt-2">
                        <span class="inline-flex gap-2 scaffold-details-small">
                            <img :src="CollabFilledIcon" alt="" aria-hidden="true" class="h-4 w-auto" /> {{ t("connections.list.header.connectedIconHint") }}
                        </span>
                    </div>
                </template>
                <template v-if="showEmptyState" #actions>
                    <AddPeopleMenu
                        :public-profile-enabled="public_profile_enabled"
                        :calendar-import-enabled="calendar_import_enabled"
                        :has-calendar-integration="has_calendar_integration"
                        :csv-import-enabled="csv_import_enabled"
                        :opens-dialog="isAddPeopleTreatment"
                        @open="openAddPeopleDialog()"
                        @calendar="navigateToCalendarImport"
                        @connect-calendar="showCalendarSettings = true"
                        @csv="showCsvImportDialog = true"
                        @organization="showOrganizationDirectory = true"
                        @manual="createConnection" />
                </template>
            </BasePageHeader>
            <HomeDisclaimerMessage v-if="disclaimer_message" :message="disclaimer_message" />
        </div>
        <div v-if="!showEmptyState" class="flex flex-wrap gap-4 justify-between items-center mb-2">
            <div class="flex flex-wrap gap-2 self-start items-center">
                <BaseToggleGroup
                    :model-value="filter"
                    :options="filterOptions"
                    appearance="chips"
                    :aria-label="t('peopleDirectory.filterPeople')"
                    @update:model-value="handleChangeFilter">
                    <template #option="{ option }">
                        <i v-if="option.key === FILTER.LINKED_PROFILES" class="bi" :class="COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS" aria-hidden="true"></i>
                        {{ option.label }}
                    </template>
                </BaseToggleGroup>
                <!-- Single meaningful facet: compact inline control -->
                <div v-if="showSingleFacetControl" class="inline-flex items-center gap-2 rounded-full border border-border-strong bg-surface-raised py-1 pl-3 pr-1">
                    <span v-if="singleFacet?.name" class="body-small-semibold uppercase tracking-[0.08em] text-ink-muted">{{ singleFacet.name }}</span>
                    <BaseSelect
                        :aria-label="t('connections.list.facetOptionsLabel', { facet: singleFacet?.name ?? '' })"
                        :model-value="singleFacetValue"
                        :options="singleFacetOptions"
                        trigger-class="h-[28px]! min-w-36! border-0! bg-surface-sunken px-3! py-2! rounded-full!"
                        @update:model-value="handleSingleFacetChange" />
                </div>
                <!-- Multiple facets: modal trigger -->
                <FacetFilterModal v-if="meaningfulFacets.length > 1" v-model="activeFacetFilters" :facets="meaningfulFacets" @update:model-value="handleFacetFiltersChange" />
            </div>
            <div v-if="isSelecting" class="flex items-center gap-2">
                <BaseButton theme="tertiary" @click="exitSelectMode">{{ t("people.bulkDelete.cancel") }}</BaseButton>
                <BaseButton theme="danger" :disabled="selectedCount === 0" @click="showBulkDeleteDialog = true">
                    {{ selectedCount > 0 ? t("people.bulkDelete.deleteCount", { count: selectedCount }) : t("people.bulkDelete.delete") }}
                </BaseButton>
            </div>
            <div v-else class="flex items-center gap-2">
                <AddPeopleMenu
                    :public-profile-enabled="public_profile_enabled"
                    :calendar-import-enabled="calendar_import_enabled"
                    :has-calendar-integration="has_calendar_integration"
                    :csv-import-enabled="csv_import_enabled"
                    :opens-dialog="isAddPeopleTreatment"
                    @open="openAddPeopleDialog()"
                    @calendar="navigateToCalendarImport"
                    @connect-calendar="showCalendarSettings = true"
                    @csv="showCsvImportDialog = true"
                    @organization="showOrganizationDirectory = true"
                    @manual="createConnection" />
                <BaseDropdownMenu :sections="pageMenuSections" @select="isSelecting = true">
                    <template #trigger>
                        <BaseButton theme="neutral" icon-only :aria-label="t('people.bulkDelete.moreOptions')">
                            <i class="bi bi-three-dots" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                </BaseDropdownMenu>
            </div>
        </div>
        <div v-if="!showEmptyState" class="md:-mt-6 flex flex-wrap gap-4 items-center">
            <BaseInput
                v-model="searchQuery"
                size="compact"
                :aria-label="t('connections.list.search.label')"
                :placeholder="t('connections.list.search.label')"
                class="w-full max-w-sm grow">
                <template #prefix><i class="bi bi-search text-ink-muted" aria-hidden="true"></i></template>
            </BaseInput>
            <BaseSelect
                v-model="sortOption"
                :aria-label="t('connections.list.sort.label')"
                :options="sortFieldOptions"
                trigger-class="h-[38px]! px-3! rounded-lg! w-48!"
                @update:model-value="filterConnections" />
            <p class="w-full body-small text-ink empty:sr-only" role="status">
                <template v-if="searchRateLimited">{{ t("peopleDirectory.searchRateLimited") }}</template>
            </p>
        </div>
        <div v-if="hasActiveFacetFilters && !showEmptyState" class="md:-mt-2 flex flex-wrap items-center gap-2">
            <span class="body-small text-ink-muted">{{ t("connections.list.activeFilters.label") }}</span>
            <BaseButton v-for="entry in activeFacetFilterEntries" :key="entry.id" theme="neutral" size="sm" class="gap-2 rounded-full px-4" @click="removeFacetFilter(entry.id)">
                <span class="body-small-semibold text-ink">{{ entry.name }}</span>
                <span class="mx-1 text-ink-muted">{{ entry.value }}</span>
                <i class="bi bi-x text-base/none" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton theme="tertiary" size="sm" @click="clearFacetFilters">{{ t("connections.list.activeFilters.clearAll") }}</BaseButton>
        </div>

        <div class="connections-grid" aria-live="polite" aria-relevant="text">
            <template v-if="allConnections.length > 0">
                <ConnectionsCard
                    v-for="conn in allConnections"
                    :key="conn.id"
                    :connection-id="conn.id"
                    :connection="conn"
                    :name="conn.persona?.name"
                    :job-title="conn.connection.job_title"
                    :notes="getNotesSection(conn)"
                    :notes-pending="!!conn.notes_pending"
                    :on-details-click="handleDetails"
                    :is-dismissed="filter === FILTER.DISMISSED"
                    :has-public-profile="!!conn.public_profile"
                    :is-hris="!!conn.is_hris"
                    :is-inactive="!!conn.is_inactive"
                    :selectable="isSelecting"
                    :selected="selectedIds.has(conn.id)"
                    @delete="onCardDelete"
                    @merge="onCardMerge"
                    @toggle-select="toggleSelected" />
            </template>
            <template v-else>
                <CollaborationProfilePreview v-if="showEmptyState && public_profile_enabled" class="col-span-full pt-3 pb-6" />
                <LinkedProfilesEmptyState v-else-if="showLinkedProfilesEmptyState" class="col-span-full" @add-from-organization="openOrganizationDirectory" />
                <p v-else class="col-span-full body-small text-ink-muted text-center" role="alert">{{ t("connections.list.noResults") }}</p>
            </template>
        </div>
        <div v-if="hasMore && !showEmptyState" class="flex justify-center pb-10">
            <BaseButton theme="neutral" :disabled="isLoadingMore" @click="loadMore">
                {{ isLoadingMore ? t("connections.list.loadMore.loading") : t("connections.list.loadMore.button") }}
            </BaseButton>
        </div>
    </BaseLayout>
    <BaseLayout v-else gap="sm" fill-height content-class="max-md:px-4 fs-mask">
        <header data-testid="connections-page-header" class="flex flex-col gap-2">
            <div class="flex items-center justify-between gap-2 md:gap-4">
                <h1 class="display-serif text-ink">{{ t("peopleDirectory.title") }}</h1>
                <div class="flex items-center gap-2">
                    <AddPeopleMenu
                        v-if="activeView === 'people'"
                        :compact="isMobile"
                        :public-profile-enabled="public_profile_enabled"
                        :calendar-import-enabled="calendar_import_enabled"
                        :has-calendar-integration="has_calendar_integration"
                        :csv-import-enabled="csv_import_enabled"
                        :opens-dialog="isAddPeopleTreatment"
                        @open="openAddPeopleDialog()"
                        @calendar="navigateToCalendarImport"
                        @connect-calendar="showCalendarSettings = true"
                        @csv="showCsvImportDialog = true"
                        @organization="showOrganizationDirectory = true"
                        @manual="createConnection" />
                    <BaseLink
                        v-if="activeView === 'teams' && create_team_url"
                        :href="create_team_url"
                        class="base-btn-primary base-btn-with-icon base-btn-icon-start"
                        :class="{ 'base-btn-sm': isMobile }">
                        <i class="bi bi-plus-lg icon-semibold" aria-hidden="true" />{{ t("peopleDirectory.createTeam.addTeam") }}
                    </BaseLink>
                    <BaseDropdownMenu v-if="activeView === 'people' && !showEmptyState && !isSelecting" :sections="pageMenuSections" @select="isSelecting = true">
                        <template #trigger>
                            <!-- Square at the height of the text button beside it; the icon-only spec alone is 4px shorter. -->
                            <BaseButton
                                theme="neutral"
                                icon-only
                                :size="isMobile ? 'sm' : 'md'"
                                class="md:size-[calc(2*var(--spacing-button-y)+var(--text-button)*var(--leading-button))]"
                                :aria-label="t('people.bulkDelete.moreOptions')">
                                <i class="bi bi-three-dots" aria-hidden="true" />
                            </BaseButton>
                        </template>
                    </BaseDropdownMenu>
                </div>
            </div>
            <p class="body-small text-ink-muted">
                {{ t("peopleDirectory.intro") }}<span class="block">{{ t("peopleDirectory.privacy") }}</span>
            </p>
            <HomeDisclaimerMessage v-if="disclaimer_message" class="mt-1" :message="disclaimer_message" />
        </header>
        <BaseNotification v-if="showImportBanner" theme="success" :text="t('peopleDirectory.calendarImported', { count: calendarImportCount })" @dismiss="dismissBanner" />
        <BaseNotification v-if="showCsvImportBanner" theme="success" :text="t('peopleDirectory.csvImported', { count: csvImportCount })" @dismiss="dismissCsvBanner" />
        <BaseNotification v-if="actionError" theme="error" :text="actionError" @dismiss="actionError = ''" />
        <BaseNotification v-if="showBulkDeleteErrorBanner" theme="error" :text="t('people.bulkDelete.error')" @dismiss="showBulkDeleteErrorBanner = false" />
        <BaseTabs
            class="grow"
            content-class="flex grow flex-col"
            :model-value="activeView"
            :tab-ids="teams_enabled ? ['teams', 'people'] : ['people']"
            :labels="{ teams: t('peopleDirectory.teams'), people: t('peopleDirectory.people') }"
            @update:model-value="changeView">
            <template #content-teams>
                <div class="flex grow flex-col pt-3 md:pt-6">
                    <p class="sr-only" role="status">
                        <template v-if="isLoadingTeams && !listError">{{ t("peopleDirectory.loadingTeams") }}</template>
                    </p>
                    <div v-if="listError" class="flex flex-wrap items-center gap-3" role="alert">
                        <p class="body-small text-ink">{{ t("peopleDirectory.teamsLoadError") }}</p>
                        <BaseButton theme="secondary" size="sm" @click="filterConnections">{{ t("peopleDirectory.retry") }}</BaseButton>
                    </div>
                    <TeamsList
                        v-else
                        v-model:search="teamSearch"
                        v-model:filter="teamFilter"
                        v-model:sort="teamSort"
                        class="grow"
                        :viewer-id="viewer_id"
                        :teams="teams"
                        :unviewed-memberships="unviewedMemberships"
                        :loading="isLoadingTeams" />
                </div>
            </template>
            <template #content-people>
                <div class="flex flex-col gap-4 pt-3 md:gap-5 md:pt-6">
                    <div class="grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-4">
                        <FormField id="people-search" :label="t('peopleDirectory.searchPeople')" hide-label class="col-span-2">
                            <BaseInput
                                v-model="searchQuery"
                                type="search"
                                :aria-label="t('peopleDirectory.searchPeople')"
                                :placeholder="t('peopleDirectory.searchPeoplePlaceholder')">
                                <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                            </BaseInput>
                        </FormField>
                        <FormField id="people-filter" :label="t('peopleDirectory.filter')" hide-label>
                            <BaseSelect
                                :model-value="filter"
                                :aria-label="t('peopleDirectory.filterPeople')"
                                :prefix="t('peopleDirectory.filterPrefix')"
                                :options="redesignFilterOptions"
                                dropdown-size="tall"
                                @update:model-value="handleChangeFilter" />
                        </FormField>
                        <FormField id="people-sort" :label="t('peopleDirectory.sort')" hide-label>
                            <BaseSelect
                                v-model="sortOption"
                                :aria-label="t('peopleDirectory.sortPeople')"
                                :prefix="t('peopleDirectory.sortPrefix')"
                                :options="redesignSortOptions"
                                dropdown-size="tall"
                                @update:model-value="filterConnections" />
                        </FormField>
                    </div>
                    <p class="body-small text-ink empty:sr-only" role="status">
                        <template v-if="searchRateLimited">{{ t("peopleDirectory.searchRateLimited") }}</template>
                    </p>
                    <div v-if="meaningfulFacets.length" class="flex flex-wrap items-center gap-3">
                        <FacetFilterModal v-model="activeFacetFilters" :facets="meaningfulFacets" @update:model-value="handleFacetFiltersChange" />
                        <BaseButton
                            v-for="entry in activeFacetFilterEntries"
                            :key="entry.id"
                            theme="secondary"
                            size="sm"
                            :aria-label="t('peopleDirectory.removeFilter', { name: entry.name, value: entry.value })"
                            @click="removeFacetFilter(entry.id)">
                            {{ t("peopleDirectory.facetValue", { name: entry.name, value: entry.value }) }}<i class="bi bi-x ml-2" aria-hidden="true" />
                        </BaseButton>
                    </div>
                    <div v-if="isSelecting" class="flex flex-wrap justify-end items-center gap-2">
                        <BaseButton theme="tertiary" @click="exitSelectMode">{{ t("people.bulkDelete.cancel") }}</BaseButton>
                        <BaseButton theme="danger" :disabled="selectedCount === 0" @click="showBulkDeleteDialog = true">
                            {{ selectedCount > 0 ? t("people.bulkDelete.deleteCount", { count: selectedCount }) : t("people.bulkDelete.delete") }}
                        </BaseButton>
                    </div>
                    <div v-if="listError" class="flex flex-wrap items-center gap-3" role="alert">
                        <p class="body-small text-ink">{{ listError }}</p>
                        <BaseButton theme="secondary" size="sm" @click="filterConnections">{{ t("peopleDirectory.retry") }}</BaseButton>
                    </div>
                    <p class="sr-only" role="status">
                        <template v-if="isLoadingPeople && !listError">{{ t("peopleDirectory.loading") }}</template>
                    </p>
                    <PeopleList
                        v-if="(allConnections.length || isLoadingPeople) && !listError"
                        :connections="allConnections"
                        :loading="isLoadingPeople"
                        :selectable="isSelecting"
                        :selected-ids="selectedIds"
                        :dismissed="filter === FILTER.DISMISSED"
                        :public-profile-enabled="public_profile_enabled"
                        :busy-id="startingChatId"
                        :details-url-template="detailsUrlWithQuery"
                        :edit-url-template="editUrlWithQuery"
                        :profile-url-template="profile_url_template"
                        @toggle-select="toggleSelected"
                        @details="(person) => logUserInteraction('connection_detail_clicked', { connection_id: person.id })"
                        @open="(person) => handleDetails(person.id)"
                        @chat="startPersonChat"
                        @role-play="startRolePlay"
                        @merge="(person) => onCardMerge(person.id)"
                        @delete="(person) => onCardDelete(person.id)"
                        @edit="(person) => router.visit(editUrlWithQuery.replace(zeroUuid, person.id))" />
                    <LinkedProfilesEmptyState v-else-if="!isLoadingConnections && !listError && showLinkedProfilesEmptyState" @add-from-organization="openOrganizationDirectory" />
                    <div
                        v-else-if="!isLoadingConnections && !listError"
                        class="flex min-h-106 flex-col items-center justify-center rounded-lg border border-border bg-surface p-6 text-center">
                        <h2 class="body-serif text-ink">{{ showEmptyState ? t("peopleDirectory.emptyTitle") : t("peopleDirectory.noMatches") }}</h2>
                        <p class="body-small mt-1 max-w-md text-ink-muted">{{ showEmptyState ? t("peopleDirectory.emptyHelp") : t("peopleDirectory.noMatchesHelp") }}</p>
                        <BaseButton v-if="!showEmptyState" theme="secondary" size="sm" class="mt-4" @click="resetPeopleFilters">{{ t("peopleDirectory.clearFilters") }}</BaseButton>
                    </div>
                    <div v-if="hasMore && !listError" class="flex flex-col items-center gap-3">
                        <p v-if="moreError" class="body-small text-ink" role="alert">{{ t("peopleDirectory.loadError") }}</p>
                        <BaseButton theme="secondary" :disabled="isLoadingMore || isLoadingConnections" @click="loadMore">{{
                            isLoadingMore ? t("peopleDirectory.loading") : moreError ? t("peopleDirectory.retry") : t("peopleDirectory.loadMore")
                        }}</BaseButton>
                    </div>
                </div>
            </template>
        </BaseTabs>
    </BaseLayout>
    <!-- Anchored instance opened from a card's kebab "Merge" action. Mounted
                 unconditionally so its open-watcher (which loads candidates) is registered
                 before `open` flips to true. -->
    <MergeConnectionsDialog v-model:open="showKebabMergeDialog" :current-connection="mergeAnchorConnection ?? undefined" @merged="filterConnections" />
    <ConfirmationDialog
        v-model:open="showDeleteDialog"
        :title="t('connections.list.deleteDialog.title')"
        :text="
            connectionPendingDelete?.persona?.name
                ? t('connections.list.deleteDialog.text', { name: connectionPendingDelete.persona.name })
                : t('connections.list.deleteDialog.textUnnamed')
        "
        :confirm-label="t('common.delete')"
        @confirm="confirmDelete" />
    <CalendarIntegrationSettings
        v-if="calendar_import_enabled && !has_calendar_integration"
        v-model:open="showCalendarSettings"
        source="connections"
        @close="showCalendarSettings = false" />
    <OrganizationProfileDirectory
        v-if="public_profile_enabled && showOrganizationDirectory"
        v-model:open="showOrganizationDirectory"
        :has-collaboration-profile="has_collaboration_profile"
        :create-profile-url="create_profile_url"
        :invite-url="collaboration_profile_invite_url"
        :source="organizationDirectorySource"
        @added="directoryChanged = true"
        @removed="directoryChanged = true" />

    <ConfirmationDialog
        v-model:open="showBulkDeleteDialog"
        :title="t('people.bulkDelete.dialogTitle', { count: selectedCount })"
        :text="t('people.bulkDelete.dialogText')"
        :confirm-label="t('people.bulkDelete.deleteCount', { count: selectedCount })"
        confirm-theme="danger"
        width="narrow"
        actions-justify="between"
        :confirm-disabled="isBulkDeleting"
        :cancel-disabled="isBulkDeleting"
        :close-on-confirm="false"
        @confirm="confirmBulkDelete" />
    <CsvImportDialog
        v-if="csv_import_enabled"
        v-model:open="showCsvImportDialog"
        :csv-template-url="csv_template_url"
        :csv-preview-url="csv_preview_url"
        :csv-import-url="csv_import_url"
        @imported="handleCsvImported" />
    <AddPeopleDialog
        v-if="isAddPeopleTreatment"
        v-model:open="showAddPeopleDialog"
        :initial-tab="addPeopleTab"
        :public-profile-enabled="public_profile_enabled"
        :calendar-import-enabled="calendar_import_enabled"
        :has-calendar-integration="has_calendar_integration"
        :csv-import-enabled="csv_import_enabled"
        :has-collaboration-profile="has_collaboration_profile"
        :create-profile-url="create_profile_url"
        :invite-url="collaboration_profile_invite_url"
        :directory-source="organizationDirectorySource"
        :calendar-people-api-url="calendar_people_api_url"
        :bulk-connection-api-url="bulk_connection_api_url"
        :csv-template-url="csv_template_url"
        :csv-preview-url="csv_preview_url"
        :csv-import-url="csv_import_url"
        @changed="directoryChanged = true"
        @calendar-imported="handleCalendarImported"
        @csv-imported="handleCsvImported" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { breakpointsTailwind, useBreakpoints, useDebounceFn } from "@vueuse/core";
import CollabFilledIcon from "~img/collab_icons/collab-filled.svg";
import { useFetch } from "~vue/api";
import type { ProfileDirectorySource } from "~vue/api/generated/collab-profiles/types.gen";
import type { UnviewedTeamMemberships } from "~vue/api/generated/product-teams";
import type { PeopleTeamSummary } from "~vue/api/generated/team-perspective/types.gen";
import HomeDisclaimerMessage from "~vue/ChatHome/HomeDisclaimerMessage.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BaseToggleGroup from "~vue/components/BaseToggleGroup.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS } from "~vue/components/navigation/menuPanes";
import { useConnectionActions } from "~vue/composables/useConnectionActions";
import { useConnectionFacetFilters } from "~vue/composables/useConnectionFacetFilters";
import AddPeopleDialog from "~vue/Connections/AddPeopleDialog.vue";
import AddPeopleMenu from "~vue/Connections/AddPeopleMenu.vue";
import { type AddPeopleTab, isAddPeopleTab } from "~vue/Connections/addPeopleTabs";
import CollaborationProfilePreview from "~vue/Connections/CollaborationProfilePreview.vue";
import ConnectionsCard from "~vue/Connections/ConnectionsCard.vue";
import CsvImportDialog from "~vue/Connections/CsvImportDialog.vue";
import FacetFilterModal from "~vue/Connections/FacetFilterModal.vue";
import LinkedProfilesEmptyState from "~vue/Connections/LinkedProfilesEmptyState.vue";
import MergeConnectionsDialog from "~vue/Connections/MergeConnectionsDialog.vue";
import OrganizationProfileDirectory from "~vue/Connections/OrganizationProfileDirectory.vue";
import PeopleList from "~vue/Connections/PeopleList.vue";
import TeamsList from "~vue/Connections/TeamsList.vue";
import { getNotesSection } from "~vue/Connections/utils";
import BaseLink from "~vue/router/BaseLink.vue";
import type { ConnectionDict, FacetDefinition } from "~vue/types/connection";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE } from "~vue/utils/constants.js";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, onUnmounted, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const FILTER = {
    ALL: "all",
    TEAM: "team",
    ORGANIZATION: "organization",
    DISMISSED: "dismissed",
    LINKED_PROFILES: "linked_profiles",
};

const { t } = useI18n();

const filterOptions = computed(() => {
    const filters = [
        { key: FILTER.ALL, label: t("connections.list.filters.all") },
        { key: FILTER.TEAM, label: t("connections.list.filters.myTeam") },
        { key: FILTER.DISMISSED, label: t("connections.list.filters.dismissed") },
    ];
    if (public_profile_enabled) {
        filters.push({ key: FILTER.LINKED_PROFILES, label: t("connections.list.filters.collaborationProfilesCount", { count: linked_profiles_count }) });
    }
    return filters;
});

const sortFieldOptions = computed(() => [
    { key: "last_discussed_desc", label: t("connections.list.sort.recentlyDiscussed") },
    { key: "last_discussed_asc", label: t("connections.list.sort.oldestDiscussed") },
    { key: "times_discussed_desc", label: t("connections.list.sort.mostDiscussed") },
    { key: "times_discussed_asc", label: t("connections.list.sort.leastDiscussed") },
    { key: "name_asc", label: t("connections.list.sort.nameAsc") },
    { key: "name_desc", label: t("connections.list.sort.nameDesc") },
]);

type ConnectionsProps = {
    teams_enabled?: boolean;
    create_team_url?: string | null;
    teams?: PeopleTeamSummary[];
    people_query?: Record<string, string>;
    viewer_id?: string;
    profile_url_template?: string;
    connection_url_template?: string;
    edit_url_template?: string;
    disclaimer_message?: string;
    connections: Array<ConnectionDict>;
    new_connection_url: string;
    has_collaboration_profile?: boolean;
    create_profile_url?: string;
    collaboration_profile_invite_url?: string;
    calendar_import_enabled?: boolean;
    has_calendar_integration?: boolean;
    from_calendar_url?: string;
    imported_count?: number | null;
    calendar_integration?: object;
    csv_import_enabled?: boolean;
    public_profile_enabled?: boolean;
    csv_template_url?: string;
    csv_preview_url?: string;
    csv_import_url?: string;
    facets?: Array<FacetDefinition>;
    has_more_connections?: boolean;
    next_offset?: number | null;
    linked_profiles_count?: number;
    calendar_people_api_url?: string;
    bulk_connection_api_url?: string;
    add_people_experiment_arm?: "control" | "treatment" | null;
};

const {
    teams_enabled = false,
    create_team_url = null,
    teams = [],
    people_query = {},
    viewer_id = "",
    profile_url_template = "",
    connection_url_template = "",
    edit_url_template = "",
    connections,
    disclaimer_message = "",
    new_connection_url,
    has_collaboration_profile = false,
    create_profile_url = "",
    collaboration_profile_invite_url = "",
    calendar_import_enabled = false,
    has_calendar_integration = false,
    from_calendar_url = "",
    imported_count = null,
    calendar_integration = {},
    csv_import_enabled = false,
    public_profile_enabled = false,
    csv_template_url = "",
    csv_preview_url = "",
    csv_import_url = "",
    facets = [],
    has_more_connections = false,
    next_offset = null,
    linked_profiles_count = 0,
    calendar_people_api_url = "",
    bulk_connection_api_url = "",
    add_people_experiment_arm = null,
} = defineProps<ConnectionsProps>();

provide("calendarIntegration", calendar_integration);

const { viewDetails, startChat, startRolePlay } = useConnectionActions();
const isMobile = useBreakpoints(breakpointsTailwind).smaller("md");
const zeroUuid = "00000000-0000-0000-0000-000000000000";
const activeView = ref(teams_enabled && people_query.view === "teams" ? "teams" : "people");
const teamSearch = ref(people_query.team_search || "");
const teamFilter = ref(people_query.team_filter || "all");
const teamSort = ref(people_query.team_sort || "name_asc");
const actionError = ref("");
const listError = ref("");
const searchRateLimited = ref(false);
const startingChatId = ref<string | null>(null);
const redesignFilterOptions = computed(() => {
    const options = [
        { key: FILTER.ALL, label: t("peopleDirectory.all") },
        { key: FILTER.TEAM, label: t("peopleDirectory.myTeam") },
        { key: FILTER.DISMISSED, label: t("peopleDirectory.dismissed") },
    ];
    if (public_profile_enabled) {
        options.push({
            key: FILTER.LINKED_PROFILES,
            label: t("peopleDirectory.collaborationProfilesCount", { count: linked_profiles_count }),
        });
    }
    return options;
});
const redesignSortOptions = computed(() => [
    { key: "last_discussed_desc", label: t("peopleDirectory.recent") },
    { key: "last_discussed_asc", label: t("peopleDirectory.oldest") },
    { key: "times_discussed_desc", label: t("peopleDirectory.most") },
    { key: "times_discussed_asc", label: t("peopleDirectory.least") },
    { key: "name_asc", label: t("peopleDirectory.nameAsc") },
    { key: "name_desc", label: t("peopleDirectory.nameDesc") },
]);
async function startPersonChat(person: ConnectionDict) {
    if (startingChatId.value) {
        return;
    }
    startingChatId.value = person.id;
    actionError.value = "";
    try {
        const url = await startChat(person, { chatSource: CHATSOURCE.CONNECTION_CARD });
        if (url) {
            router.visit(url);
        } else {
            actionError.value = t("peopleDirectory.actionError");
        }
    } catch {
        actionError.value = t("peopleDirectory.actionError");
    } finally {
        startingChatId.value = null;
    }
}
function changeView(view: string | undefined) {
    exitSelectMode();
    activeView.value = teams_enabled && view === "teams" ? "teams" : "people";
    filterConnections();
}
function resetPeopleFilters() {
    filter.value = FILTER.ALL;
    searchQuery.value = "";
    activeFacetFilters.value = {};
    filterConnections();
}

const filter = ref(people_query.filter || FILTER.ALL);
const searchQuery = ref(people_query.search || "");
const sortOption = ref(`${people_query.sort_field || "last_discussed"}_${people_query.sort_dir || "desc"}`);
const calendarImportCount = ref(imported_count ?? 0);
const showImportBanner = ref(calendarImportCount.value > 0);
const showCalendarSettings = ref(false);
const showCsvImportDialog = ref(false);
const showOrganizationDirectory = ref(false);
const organizationDirectorySource = computed<ProfileDirectorySource>(() => (filter.value === FILTER.LINKED_PROFILES ? "collaboration_profile_filter" : "my_people"));
const isAddPeopleTreatment = add_people_experiment_arm === "treatment";
const showAddPeopleDialog = ref(false);
const addPeopleTab = ref<AddPeopleTab>("directory");
const openAddPeopleDialog = (tab: AddPeopleTab = "directory") => {
    addPeopleTab.value = tab;
    showAddPeopleDialog.value = true;
};
const openOrganizationDirectory = () => {
    if (isAddPeopleTreatment) {
        openAddPeopleDialog();
    } else {
        showOrganizationDirectory.value = true;
    }
};
const directoryChanged = ref(false);
watch([showOrganizationDirectory, showAddPeopleDialog], ([isDirectoryOpen, isAddPeopleOpen]) => {
    if (!isDirectoryOpen && !isAddPeopleOpen && directoryChanged.value) {
        directoryChanged.value = false;
        filterConnections();
    }
});
const showCsvImportBanner = ref(false);
const csvImportCount = ref(0);
const isLoadingConnections = ref(false);
// The view a pending request was started for, so the spinner stays on that table rather than following tab switches.
const loadingView = ref<"teams" | "people">("people");
const isLoadingTeams = computed(() => isLoadingConnections.value && loadingView.value === "teams");
const isLoadingPeople = computed(() => isLoadingConnections.value && loadingView.value === "people");

// Delete flow (kebab menu): "Delete person" opens a confirmation, then DELETEs it.
const showDeleteDialog = ref(false);
const connectionPendingDelete = ref<ConnectionDict | null>(null);
const deleteUrl = computed(() => `/api/connections/${connectionPendingDelete.value?.id ?? ""}/`);
const { execute: executeDelete, onFetchResponse: onDeleteResponse, onFetchError: onDeleteError } = useFetch(deleteUrl, { immediate: false }).delete();

const onCardDelete = (id: string) => {
    connectionPendingDelete.value = allConnections.value.find((c) => c.id === id) ?? null;
    if (connectionPendingDelete.value) {
        showDeleteDialog.value = true;
    }
};
const confirmDelete = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_DELETE, { source: "connections_kebab_menu" });
    executeDelete();
};
onDeleteResponse(() => {
    showDeleteDialog.value = false;
    connectionPendingDelete.value = null;
    filterConnections();
});
onDeleteError(() => {
    showDeleteDialog.value = false;
    connectionPendingDelete.value = null;
    actionError.value = t("peopleDirectory.actionError");
});

// Bulk delete flow (page "..." menu): pick cards, confirm, then delete them in one request.
const pageMenuSections = computed<DropdownMenuSection[]>(() => [
    { id: "bulk", items: [{ id: "delete-people", type: "button", label: t("people.bulkDelete.menuLabel"), icon: "bi bi-trash", theme: "danger" }] },
]);
const isSelecting = ref(false);
const selectedIds = ref(new Set<string>());
const selectedCount = computed(() => selectedIds.value.size);
const showBulkDeleteDialog = ref(false);
const showBulkDeleteErrorBanner = ref(false);
const bulkDeletePayload = computed(() => ({ ids: [...selectedIds.value] }));
const {
    execute: executeBulkDelete,
    isFetching: isBulkDeleting,
    onFetchResponse: onBulkDeleteResponse,
    onFetchError: onBulkDeleteError,
} = useFetch("/api/connections/bulk-delete/", { immediate: false }).post(bulkDeletePayload).json();

const toggleSelected = (id: string) => {
    const next = new Set(selectedIds.value);
    if (next.has(id)) {
        next.delete(id);
    } else {
        next.add(id);
    }
    selectedIds.value = next;
};
const exitSelectMode = () => {
    isSelecting.value = false;
    selectedIds.value = new Set();
};
const confirmBulkDelete = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTIONS_BULK_DELETE, { count: selectedCount.value });
    showBulkDeleteErrorBanner.value = false;
    executeBulkDelete();
};
onBulkDeleteResponse(() => {
    showBulkDeleteDialog.value = false;
    exitSelectMode();
    filterConnections();
});
onBulkDeleteError(() => {
    showBulkDeleteDialog.value = false;
    showBulkDeleteErrorBanner.value = true;
});

// Merge flow (kebab menu): "Merge" on a card opens the merge dialog anchored to that person.
const showKebabMergeDialog = ref(false);
const mergeAnchorConnection = ref<ConnectionDict | null>(null);
const onCardMerge = (id: string) => {
    mergeAnchorConnection.value = allConnections.value.find((c) => c.id === id) ?? null;
    if (mergeAnchorConnection.value) {
        showKebabMergeDialog.value = true;
    }
};

// Pagination state
const allConnections = ref([...connections]);
const currentOffset = ref(next_offset);
const hasMore = ref(has_more_connections);

let syncingFromServer = false;
let listGeneration = 0;
const debouncedSearchQuery = useDebounceFn(() => {
    if (!syncingFromServer) {
        filterConnections();
    }
}, 500);

watch(
    searchQuery,
    () => {
        if (syncingFromServer) {
            return;
        }
        loadingView.value = "people";
        isLoadingConnections.value = true;
        debouncedSearchQuery();
    },
    { flush: "sync" },
);

const queryParams = computed(() => {
    const lastUnderscore = sortOption.value.lastIndexOf("_");
    const sortField = sortOption.value.slice(0, lastUnderscore);
    const sortDir = sortOption.value.slice(lastUnderscore + 1);
    const params: Record<string, string> = {
        filter: filter.value,
        sort_field: sortField,
        sort_dir: sortDir,
    };
    if (searchQuery.value.trim()) {
        params.search = searchQuery.value.trim();
    }
    if (Object.keys(normalizedFacetFilters.value).length > 0) {
        params.facet_filters = JSON.stringify(normalizedFacetFilters.value);
    }
    if (teams_enabled) {
        Object.assign(params, {
            view: activeView.value,
            team_search: teamSearch.value,
            team_filter: teamFilter.value,
            team_sort: teamSort.value,
        });
    }
    return params;
});

const detailsUrlWithQuery = computed(() => `${connection_url_template}?${new URLSearchParams(queryParams.value)}`);
const editUrlWithQuery = computed(() => `${edit_url_template}?${new URLSearchParams(queryParams.value)}`);

const filterConnections = () => {
    const generation = ++listGeneration;
    listError.value = "";
    searchRateLimited.value = false;
    loadingView.value = activeView.value === "teams" ? "teams" : "people";
    isLoadingConnections.value = true;
    router.get("/people/", queryParams.value, {
        preserveState: true,
        // The redesigned directory shows loading inside the table instead of the page-wide progress bar.
        showProgress: !teams_enabled,
        only: ["connections", "has_more_connections", "next_offset", "people_query", "teams_enabled", "teams", "linked_profiles_count", "errors"],
        onFinish: (visit) => {
            if (generation !== listGeneration) {
                return;
            }
            isLoadingConnections.value = false;
            // Reset pagination state from new SSR props
            allConnections.value = [...connections];
            hasMore.value = has_more_connections;
            currentOffset.value = next_offset;
            if (!visit.cancelled && !listError.value && !searchRateLimited.value) {
                saveTeamQuery();
            }
        },
        onError: (error) => {
            // The server skips a rate-limited search and leaves the props alone, so the current results stay visible.
            if (error.search) {
                searchRateLimited.value = true;
                return;
            }
            listError.value = t("peopleDirectory.loadError");
            logError("Failed to change filter:", error);
        },
    });
};

const {
    activeFacetFilters,
    activeFacetFilterEntries,
    clearFacetFilters,
    handleFacetFiltersChange,
    handleSingleFacetChange,
    hasActiveFacetFilters,
    meaningfulFacets,
    normalizeActiveFacetFilters,
    normalizedFacetFilters,
    removeFacetFilter,
    showSingleFacetControl,
    singleFacet,
    singleFacetOptions,
    singleFacetValue,
} = useConnectionFacetFilters(
    computed(() => facets),
    filterConnections,
);

// "Load more" pagination via coach_api
const loadMoreUrl = computed(() => {
    const params = new URLSearchParams(queryParams.value);
    params.set("offset", String(currentOffset.value));
    params.set("page_size", "24");
    return `/v1/connections/?${params.toString()}`;
});

// Rate-limited while a search term is present; a residual 429 must not write
// its error body into `moreData` (it has no `connections` field to append).
const { data: moreData, isFetching: isLoadingMore, error: moreError, execute: fetchMoreConnections } = useFetch(loadMoreUrl, { immediate: false, updateDataOnError: false }).json();

async function loadMore() {
    if (currentOffset.value === null || !hasMore.value || isLoadingMore.value || isLoadingConnections.value) {
        return;
    }
    const requestQuery = JSON.stringify(queryParams.value);
    const generation = listGeneration;
    await fetchMoreConnections();
    if (generation !== listGeneration || requestQuery !== JSON.stringify(queryParams.value)) {
        return;
    }
    if (moreError.value) {
        logError("Failed to load more connections:", moreError.value);
        return;
    }
    if (moreData.value) {
        allConnections.value = [...allConnections.value, ...moreData.value.connections];
        hasMore.value = moreData.value.has_more;
        currentOffset.value = moreData.value.next_offset;
    }
}

const handleChangeFilter = (value: string) => {
    loadingView.value = "people";
    isLoadingConnections.value = true;
    filter.value = value;
    filterConnections();
};

const handleDetails = (id: string) => {
    if (teams_enabled) {
        logUserInteraction("connection_detail_clicked", { connection_id: id });
        router.visit((filter.value === FILTER.DISMISSED ? editUrlWithQuery.value : detailsUrlWithQuery.value).replace(zeroUuid, id));
    } else if (filter.value === FILTER.DISMISSED) {
        router.visit(`/people/edit/${id}/`);
    } else {
        viewDetails(id);
    }
};

const createConnection = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_ADD_BUTTON_CLICKED);
    router.visit(new_connection_url);
};

const handleHelpLinkClick = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTIONS_HELP_LINK_CLICKED);
};

const showLinkedProfilesEmptyState = computed(() => filter.value === FILTER.LINKED_PROFILES && !searchQuery.value.trim() && !hasActiveFacetFilters.value);

watch(
    filter,
    (newFilter) => {
        if (newFilter === FILTER.LINKED_PROFILES) {
            logUserInteraction(AmplitudeEventNames.COLLABORATION_PROFILE_FILTER_VIEWED, { source: "my_people" });
        }
    },
    { immediate: true },
);

const navigateToCalendarImport = () => {
    if (!has_calendar_integration) {
        return;
    }
    logUserInteraction(AmplitudeEventNames.CALENDAR_IMPORT_BUTTON_CLICKED, { has_calendar_integration });
    router.visit(from_calendar_url);
};

const handleCsvImported = (result: { created?: number; updated?: number }) => {
    csvImportCount.value = (result?.created || 0) + (result?.updated || 0);
    showCsvImportBanner.value = csvImportCount.value > 0;
    filterConnections();
};

const handleCalendarImported = ({ created }: { created: number }) => {
    calendarImportCount.value = created;
    showImportBanner.value = created > 0;
    filterConnections();
};

const dismissCsvBanner = () => {
    showCsvImportBanner.value = false;
};

const dismissBanner = () => {
    showImportBanner.value = false;
    // Update URL to remove import params
    const url = new URL(globalThis.location.href);
    url.searchParams.delete("imported");
    url.searchParams.delete("linked");
    globalThis.history.replaceState({}, "", url);
};

const showEmptyState = computed(
    () => !isLoadingConnections.value && allConnections.value.length === 0 && filter.value === FILTER.ALL && !searchQuery.value.trim() && !hasActiveFacetFilters.value,
);

watch(
    () => people_query,
    (query) => {
        if (!teams_enabled || isLoadingConnections.value) {
            return;
        }
        syncingFromServer = true;
        activeView.value = teams_enabled && query.view === "teams" ? "teams" : "people";
        filter.value = query.filter || FILTER.ALL;
        searchQuery.value = query.search || "";
        sortOption.value = `${query.sort_field || "last_discussed"}_${query.sort_dir || "desc"}`;
        teamSearch.value = query.team_search || "";
        teamFilter.value = query.team_filter || "all";
        teamSort.value = query.team_sort || "name_asc";
        allConnections.value = [...connections];
        hasMore.value = has_more_connections;
        currentOffset.value = next_offset;
        try {
            activeFacetFilters.value = normalizeActiveFacetFilters(JSON.parse(query.facet_filters || "{}"));
        } catch {
            activeFacetFilters.value = {};
        }
        syncingFromServer = false;
    },
);
function saveTeamQuery() {
    if (teams_enabled && activeView.value === "teams" && !isLoadingConnections.value) {
        const query = { ...queryParams.value };
        router.replace({
            url: `/people/?${new URLSearchParams(query)}`,
            props: (current) => ({ ...current, people_query: query }),
            preserveState: true,
            preserveScroll: true,
        });
    }
}
watch(
    [teamSearch, teamFilter, teamSort],
    () => {
        if (!syncingFromServer) {
            saveTeamQuery();
        }
    },
    { flush: "sync" },
);

const removeExceptionListener = router.on("exception", (event) => {
    if (teams_enabled && isLoadingConnections.value) {
        listError.value = t("peopleDirectory.loadError");
        isLoadingConnections.value = false;
        logError(event.detail.exception, { tags: { surface: "people_directory" } });
        return false;
    }
});
const removeInvalidListener = router.on("invalid", (event) => {
    if (teams_enabled && isLoadingConnections.value) {
        listError.value = t("peopleDirectory.loadError");
        isLoadingConnections.value = false;
        logError(`People directory request failed (${event.detail.response.status})`);
        return false;
    }
});
onUnmounted(() => {
    removeExceptionListener();
    removeInvalidListener();
});

const unviewedMemberships = ref<UnviewedTeamMemberships["memberships"]>([]);
onMounted(async () => {
    if (teams_enabled) {
        const { data, error } = await useFetch("/v1/teams/membership-notifications").json<UnviewedTeamMemberships>();
        if (!error.value && data.value) {
            unviewedMemberships.value = data.value.memberships;
        } else {
            logError("Failed to load team membership notifications");
        }
    }
});

onMounted(() => {
    syncingFromServer = true;
    const searchParams = new URLSearchParams(globalThis.location.search);
    // The calendar connect step returns here from OAuth with the tab to reopen.
    const addPeopleTabParam = searchParams.get("add_people_tab");
    if (addPeopleTabParam !== null) {
        if (isAddPeopleTreatment && isAddPeopleTab(addPeopleTabParam)) {
            openAddPeopleDialog(addPeopleTabParam);
        }
        searchParams.delete("add_people_tab");
        const query = searchParams.toString();
        const { pathname, hash } = globalThis.location;
        router.replace({ url: `${pathname}${query ? `?${query}` : ""}${hash}`, preserveState: true, preserveScroll: true });
    }
    const filterParam = searchParams.get("filter");
    const searchParam = searchParams.get("search");
    const sortFieldParam = searchParams.get("sort_field") ?? "last_discussed";
    const sortDirParam = searchParams.get("sort_dir") ?? "desc";

    filter.value = filterParam ?? FILTER.ALL;
    searchQuery.value = searchParam ?? "";
    sortOption.value = `${sortFieldParam}_${sortDirParam}`;

    const facetFiltersParam = searchParams.get("facet_filters");
    if (facetFiltersParam) {
        try {
            activeFacetFilters.value = normalizeActiveFacetFilters(JSON.parse(facetFiltersParam));
        } catch {
            /* ignore invalid JSON */
        }
    }
    syncingFromServer = false;
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
.connections-grid {
    @apply grid gap-4 pb-10;
    grid-template-columns: repeat(auto-fill, minmax(min(327px, 100%), 1fr));
}
</style>
